import { ReservationState } from '@orcar/common';
import {
  ALERT_MESSAGE_TYPE,
  authAtom,
  cn,
  CommonModal,
  convertToKST,
  CustomerForm,
  ENVIRONMENT,
  FinalConfirmModal,
  getDiscountList,
  getDriverAgeModalSubText,
  getSureUserId,
  INSURANCE_TYPE,
  isMobileAtom,
  KakaoPayIcon,
  ListStyle,
  MUTATION_KEY,
  NaverPayIcon,
  OrderConfirmArea,
  OrderConfirmationInsuranceInfo,
  PAGE_NAME,
  PaycoIcon,
  PAYMENT_METHOD_ID,
  PAYMENT_METHOD_LABEL,
  PaymentInfoCard,
  PaymentMethodArea,
  SamsungPayIcon,
  SESSION_STORAGE_KEY,
  TossPayIcon,
  URLs,
  useConfirmVerificationCode,
  useCustomerForm,
  useGetDiscountPolicy,
  useGetPreReservation,
  useGetVehicleModel,
  useRecoil,
  useSendVerificationCode,
  useToast,
  useUpdatePreReservation,
  useVehicleTag,
} from '@orcar/common-d2c';
import { useQueryClient } from '@tanstack/react-query';
import { loadTossPayments } from '@tosspayments/payment-sdk';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  trackPageView,
  trackPaymentFailed,
  trackPaymentStarted,
  trackPaymentSuccessful,
} from '@/utils/analytics';
import CancellationTable from './components/CancellationTable';
import RefundConfirm from './components/RefundConfirm';

const CLIENT_KEY = `${import.meta.env.VITE_TOSS_CLIENT_KEY}`;
const ORIGIN_URL = `${import.meta.env.VITE_ORIGIN_URL}`;

const PAYMENT_METHODS = [
  {
    id: PAYMENT_METHOD_ID.NAVER_PAY,
    label: PAYMENT_METHOD_LABEL.NAVER_PAY,
    iconImgUrl: NaverPayIcon,
    iconSize: 65,
  },
  {
    id: PAYMENT_METHOD_ID.KAKAO_PAY,
    label: PAYMENT_METHOD_LABEL.KAKAO_PAY,
    iconImgUrl: KakaoPayIcon,
    iconSize: 39,
  },
  {
    id: PAYMENT_METHOD_ID.TOSS_PAY,
    label: PAYMENT_METHOD_LABEL.TOSS_PAY,
    iconImgUrl: TossPayIcon,
    iconSize: 84,
  },
  {
    id: PAYMENT_METHOD_ID.SAMSUNG_PAY,
    label: PAYMENT_METHOD_LABEL.SAMSUNG_PAY,
    iconImgUrl: SamsungPayIcon,
    iconSize: 110,
  },
  {
    id: PAYMENT_METHOD_ID.PAYCO,
    label: PAYMENT_METHOD_LABEL.PAYCO,
    iconImgUrl: PaycoIcon,
    iconSize: 53,
  },
  {
    id: PAYMENT_METHOD_ID.NORMAL,
    label: PAYMENT_METHOD_LABEL.NORMAL,
  },
  {
    id: PAYMENT_METHOD_ID.BUSINESS,
    label: PAYMENT_METHOD_LABEL.BUSINESS,
  },
];

const PaymentPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sureUserId = getSureUserId();

  const { addToast } = useToast();
  const { recoilValue: isMobile } = useRecoil(isMobileAtom);
  const { recoilValue: user } = useRecoil(authAtom);

  const [paymentMethod, setPaymentMethod] = useState<PAYMENT_METHOD_ID>(
    PAYMENT_METHOD_ID.NAVER_PAY,
  );
  const [isLoadingTossClient, setIsLoadingTossClient] = useState(false);
  const [isDriverAgeModalOpen, setIsDriverAgeModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isFullCoverage, setIsFullCoverage] = useState(false);

  const preReservationId = sessionStorage.getItem(
    SESSION_STORAGE_KEY.PRE_RESERVATION_ID,
  );

  const { data: discountPolicyData } = useGetDiscountPolicy();
  const { data: preReservationData } = useGetPreReservation(preReservationId, {
    isFullCoverage,
    sureUserId: sureUserId ? Number(sureUserId) : undefined,
  });
  const { data: vehicleModelData } = useGetVehicleModel({
    id: preReservationData?.vehicleModelId,
    params: {
      insuranceName: preReservationData?.insuranceName,
      pickUpAt: convertToKST(preReservationData?.pickUpAt),
      dropOffAt: convertToKST(preReservationData?.dropOffAt),
    },
  });

  const { mutate: updatePreReservation } = useUpdatePreReservation();
  const { mutate: sendAuthenticationNumber } = useSendVerificationCode();
  const { mutate: verifyAuthenticationNumber } = useConfirmVerificationCode();

  const isMutatingUpdatePreReservation =
    queryClient.isMutating({
      mutationKey: [MUTATION_KEY.UPDATE_PRE_RESERVATION],
    }) > 0;

  const { options } = useVehicleTag(vehicleModelData);
  const {
    customerFormProps,
    checked,
    handleCheckbox,
    setIsPhoneVerified,
    setIsPhoneAuthRequested,
    setVerifiedPhoneNumber,
  } = useCustomerForm({
    user,
    options: { requireEmail: true, requirePhoneVerification: true },
  });

  const {
    phoneNumberInput,
    phoneAuthInput,
    isPhoneVerified,
    isPhoneAuthRequested,
  } = customerFormProps;

  const insuranceName = preReservationData?.insuranceName;
  const isPerfectInsurance = insuranceName === INSURANCE_TYPE.PERFECT;
  const isSuperInsurance = insuranceName === INSURANCE_TYPE.SUPER;
  const isNormalInsurance = insuranceName === INSURANCE_TYPE.NORMAL;
  const canAddUnlimitedOption =
    vehicleModelData?.insurance.insuranceName === INSURANCE_TYPE.NORMAL
      ? !!vehicleModelData?.availableWithInsuranceOption1
      : vehicleModelData?.insurance.insuranceName === INSURANCE_TYPE.PERFECT
        ? !!vehicleModelData?.availableWithInsuranceOption2
        : false;

  const handlePayment = async () => {
    if (!preReservationData) {
      alert(ALERT_MESSAGE_TYPE.INVALID_RESERVATION);
      navigate(PAGE_NAME.search);
      return;
    }
    setIsLoadingTossClient(true);

    sessionStorage.removeItem(SESSION_STORAGE_KEY.REFERRAL_CODE);

    if (isFullCoverage) {
      sessionStorage.setItem(SESSION_STORAGE_KEY.IS_FULL_COVERAGE, 'true');
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY.IS_FULL_COVERAGE);
    }

    const tossPayments = loadTossPayments(CLIENT_KEY);
    const commonOptions = {
      amount: preReservationData.price,
      orderId: preReservationData.id,
      orderName: preReservationData.vehicleModel.name || '',
      customerName: preReservationData?.customerName || '',
      successUrl: `${ORIGIN_URL}${PAGE_NAME.request_payment}`,
      failUrl: `${ORIGIN_URL}${PAGE_NAME.payment_fail}`,
    };

    const handleSuccessPayment = () => {
      // 결제 성공 이벤트 트래킹
      trackPaymentSuccessful({
        reservation_id: preReservationData.id,
        car_id: String(vehicleModelData?.id),
        car_type: vehicleModelData?.type || '',
        total_amount: preReservationData.price,
      });
      setIsLoadingTossClient(false);
    };

    const handleCancelPayment = (error: any) => {
      // 결제 실패 이벤트 트래킹
      trackPaymentFailed({
        reservation_id: preReservationData.id,
        error_code: error.code,
        error_message: error.message,
      });

      if (error.code === 'USER_CANCEL') {
        console.log('결제가 취소되었어요.');
      } else if (error.code === 'INVALID_CARD_COMPANY') {
        alert(ALERT_MESSAGE_TYPE.INVALID_CARD);
      }
      setIsLoadingTossClient(false);
    };

    if (
      paymentMethod === PAYMENT_METHOD_ID.NORMAL ||
      paymentMethod === PAYMENT_METHOD_ID.BUSINESS
    ) {
      (await tossPayments)
        .requestPayment('카드', commonOptions)
        .then(handleSuccessPayment)
        .catch(handleCancelPayment);
    } else if (
      paymentMethod === PAYMENT_METHOD_ID.NAVER_PAY ||
      paymentMethod === PAYMENT_METHOD_ID.TOSS_PAY ||
      paymentMethod === PAYMENT_METHOD_ID.KAKAO_PAY ||
      paymentMethod === PAYMENT_METHOD_ID.SAMSUNG_PAY ||
      paymentMethod === PAYMENT_METHOD_ID.PAYCO
    ) {
      (await tossPayments)
        .requestPayment('카드', {
          ...commonOptions,
          flowMode: 'DIRECT',
          easyPay: paymentMethod,
        })
        .then(handleSuccessPayment)
        .catch(handleCancelPayment);
    } else {
      return setIsLoadingTossClient(false);
    }
  };

  const handleAddUnlimitedInsurance = () => {
    setIsFullCoverage(!isFullCoverage);
  };

  const handlePhoneNumberAuth = () => {
    phoneNumberInput.checkValidity({
      isPhoneAuthRequested: true,
      value: phoneNumberInput.value,
    });

    sendAuthenticationNumber(
      { phoneNumber: phoneNumberInput.value },
      {
        onSuccess: () => {
          isMobile
            ? addToast({ content: ALERT_MESSAGE_TYPE.REQUEST_AUTH_NUMBER })
            : alert(ALERT_MESSAGE_TYPE.REQUEST_AUTH_NUMBER);

          setIsPhoneAuthRequested(true);
          phoneNumberInput.onFocus();
        },
      },
    );
  };

  const handlePhoneAuthVerification = () => {
    phoneAuthInput.checkValidity({
      isPhoneVerified: true,
      value: phoneAuthInput.value,
    });

    verifyAuthenticationNumber(
      {
        phoneNumber: phoneNumberInput.value,
        code: phoneAuthInput.value,
      },
      {
        onSuccess: () => {
          isMobile
            ? addToast({ content: ALERT_MESSAGE_TYPE.SUCCESS_AUTH })
            : alert(ALERT_MESSAGE_TYPE.SUCCESS_AUTH);

          setIsPhoneVerified(true);
          setIsPhoneAuthRequested(false);
          setVerifiedPhoneNumber(phoneNumberInput.value);
          phoneNumberInput.checkValidity({
            isPhoneVerified: true,
            value: phoneNumberInput.value,
            verifiedPhoneNumber: phoneNumberInput.value,
          });
        },
      },
    );
  };

  useEffect(() => {
    if (!preReservationId) {
      alert(ALERT_MESSAGE_TYPE.NOT_ALLOWED_ACCESS);
      navigate(PAGE_NAME.index);
      return;
    }

    trackPageView('payment_page');
  }, [preReservationId]);

  useEffect(() => {
    if (!!preReservationData?.reservationId) {
      if (preReservationData.reservation?.state === ReservationState.CANCELED) {
        alert(ALERT_MESSAGE_TYPE.INVALID_RESERVATION);
        navigate(PAGE_NAME.index);
      } else {
        alert(ALERT_MESSAGE_TYPE.PAID_RESERVATION);
        navigate(PAGE_NAME.index);
      }
    }
  }, [preReservationData]);

  return (
    <LayoutWrapper isMobile={isMobile}>
      <ContentWrapper isMobile={isMobile}>
        <SectionTitle isMobile={isMobile}>결제 진행</SectionTitle>
        <WhiteBgWrapper isMobile={isMobile}>
          <OrderConfirmArea
            modelName={vehicleModelData?.name || ''}
            options={options}
            pickUpTime={dayjs(preReservationData?.pickUpAt).tz('Asia/seoul')}
            dropOffTime={dayjs(preReservationData?.dropOffAt).tz('Asia/seoul')}
            companyName={preReservationData?.companyName || ''}
          />
        </WhiteBgWrapper>
        <WhiteBgWrapper isMobile={isMobile}>
          <CustomerForm
            {...customerFormProps}
            isPhoneVerified={isPhoneVerified}
            isPhoneAuthRequested={isPhoneAuthRequested}
            phoneAuthHandler={handlePhoneNumberAuth}
            handleVerifyPhoneAuth={handlePhoneAuthVerification}
          />
        </WhiteBgWrapper>
        <WhiteBgWrapper isMobile={isMobile}>
          <OrderConfirmationInsuranceInfo
            pricingVersion={vehicleModelData?.insurance.pricingVersion}
            insuranceInfo={vehicleModelData?.insurance.insuranceInfo}
            isMobile={isMobile}
            insuranceId={preReservationData?.insuranceName || ''}
            fullCoverageInsurancePrice={
              preReservationData?.fullCoverageInsurancePrice
            }
            onClick={handleAddUnlimitedInsurance}
            addInsurance={isFullCoverage && !isSuperInsurance}
            companyName={preReservationData?.companyName || ''}
            canAddUnlimitedOption={canAddUnlimitedOption}
            coverageLimit={
              isPerfectInsurance || isSuperInsurance
                ? vehicleModelData?.insurance2?.limit || ''
                : isNormalInsurance
                  ? vehicleModelData?.insurance1?.limit || ''
                  : ''
            }
            deductible={
              insuranceName === INSURANCE_TYPE.NORMAL
                ? vehicleModelData?.insurance1?.indemnification || ''
                : insuranceName === INSURANCE_TYPE.PERFECT
                  ? vehicleModelData?.insurance2?.indemnification || ''
                  : ''
            }
            lossOfUseFee={
              insuranceName === INSURANCE_TYPE.NORMAL
                ? vehicleModelData?.insurance1?.compensation || ''
                : insuranceName === INSURANCE_TYPE.PERFECT
                  ? vehicleModelData?.insurance2?.compensation || ''
                  : ''
            }
          />
        </WhiteBgWrapper>
        <WhiteBgWrapper isMobile={isMobile}>
          <PaymentMethodArea
            paymentMethodList={PAYMENT_METHODS}
            selectedPaymentMethod={paymentMethod}
            setSelectedPaymentMethod={setPaymentMethod}
          />
        </WhiteBgWrapper>
      </ContentWrapper>
      <div
        className={cn(
          'sticky top-36 h-fit w-96 rounded-m bg-grey-000 shadow-050',
          isMobile && 'w-full shadow-none',
        )}
      >
        <PaymentInfoCard
          isLogin={!!user}
          isMobile={isMobile}
          insurance={insuranceName}
          addInsurance={isFullCoverage && !isSuperInsurance}
          listPrice={preReservationData?.listPrice || 0}
          insurancePrice={
            preReservationData?.pricingVersion === 1
              ? preReservationData.insurancePrice
              : undefined
          }
          price={preReservationData?.price || 0}
          discountList={getDiscountList(preReservationData?.tempDiscounts)}
          fullCoverageInsurancePrice={
            preReservationData?.fullCoverageInsurancePrice || 0
          }
          onClick={() => {
            trackPaymentStarted();
            setIsDriverAgeModalOpen(true);
          }}
          isCheckBoxChecked={checked}
          onCheckBoxClicked={handleCheckbox}
          memberDiscountRate={discountPolicyData?.discountValue}
          isMemberDiscountActive={
            !!discountPolicyData && discountPolicyData.isActive
          }
          isPaymentPage
          platform='family'
        />
      </div>
      {isPaymentModalOpen && (
        <FinalConfirmModal
          buttonLoading={isLoadingTossClient}
          onClose={() => setIsPaymentModalOpen(false)}
          handleTossPayment={handlePayment}
          RuleConfirm={CancellationTable}
          RefundConfirm={RefundConfirm}
        />
      )}
      {isDriverAgeModalOpen && (
        <CommonModal
          mainText={`운전자 나이가 정말 만 ${vehicleModelData?.driverAge}세 이상인가요?`}
          subText={getDriverAgeModalSubText(
            vehicleModelData?.driverAge || 0,
            dayjs(preReservationData?.pickUpAt).format(),
          )}
          onClick={() => {
            if (!preReservationData) return;

            updatePreReservation(
              {
                id: preReservationData?.id,
                input: {
                  customerName: customerFormProps.nameInput.value,
                  customerPhoneNumber: phoneNumberInput.value,
                  customerEmail: customerFormProps.emailInput.value,
                },
              },
              {
                onSuccess: () => {
                  setIsDriverAgeModalOpen(false);
                  setIsPaymentModalOpen(true);
                },
                onError: () => {
                  alert(ALERT_MESSAGE_TYPE.ERROR_OCCURRENCE);
                  setIsDriverAgeModalOpen(false);
                },
              },
            );
          }}
          buttonText='예'
          buttonLoading={isMutatingUpdatePreReservation}
          onClose={() => setIsDriverAgeModalOpen(false)}
        />
      )}
    </LayoutWrapper>
  );
};

export default PaymentPage;

const LayoutWrapper = styled.div<{
  isMobile: boolean;
}>`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: ${({ isMobile }) => (isMobile ? '0' : '24px')};
  width: ${({ isMobile }) => (isMobile ? '100%' : '1200px')};
  ${({ isMobile }) => isMobile && 'padding: 40px 0 0'};
`;

const ContentWrapper = styled.div<{
  hasPadding?: boolean;
  isMobile?: boolean;
}>`
  width: ${({ isMobile }) => (isMobile ? '100%' : '792px')};
  scroll-margin: 150px;
`;

const SectionTitle = styled.p<{
  isMobile: boolean;
}>`
  margin-bottom: 20px;
  padding: 0 20px;
  ${({ isMobile, theme }) =>
    isMobile ? theme.NEW_FONT.H4 : theme.NEW_FONT.H3};
`;

const WhiteBgWrapper = styled.div<{
  isMobile: boolean;
}>`
  width: 100%;
  gap: 30px;
  padding: ${({ isMobile }) => (isMobile ? '0 20px' : '0 30px')};
  margin-bottom: ${({ isMobile }) => (isMobile ? '10px' : '24px')};
  background-color: ${({ theme }) => theme.COLOR.WHITE};
  background-color: white;
  border-radius: ${({ theme }) => theme.BORDER_RADIUS.LEVEL_02};

  & > *:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.COLOR.GREY_05};
  }

  & li {
    ${ListStyle}
  }
`;
